import iconWealth from '@/assets/icon-wealth.svg';
import iconGroup from '@/assets/icon-group.svg';
import iconAlternative from '@/assets/icon-alternative.svg';
import { Button } from '@/components/ui/button';
import { ArrowUpRight } from '@phosphor-icons/react';
import { BusinessUnitCard, Tag, type BusinessUnitPill } from '@/components/ds';
import { useSectionHeader } from '@/hooks/useSectionHeaders';
import { RenderHeaderParts } from '@/components/ds/RenderCMSHeader';
import { useSectionVisibility } from '@/hooks/useSectionVisibility';
import { useContentHover } from '@/hooks/useContentHover';
import { trackEvent, ANALYTICS_EVENTS } from '@/lib/analytics';

// ⚠️ ANALYTICS: Card hover wrapper for business unit cards
const BusinessCardWrapper = ({ unit, index, children }: {
  unit: { title: string };
  index: number;
  children: React.ReactNode;
}) => {
  const { contentHoverProps } = useContentHover({
    contentId: `business-${index}`,
    contentType: 'card',
    contentTitle: unit.title,
    contentSection: 'operating-businesses',
    contentPage: 'about',
    contentPosition: index,
  });

  return <div {...contentHoverProps}>{children}</div>;
};

/**
 * Business Units Section (About page)
 * 
 * CMS Integration:
 * - Uses useSectionHeader('about', 'operating-businesses') for header/tag/subheader
 */

interface BusinessUnit {
  title: string;
  description: string;
  icon: string;
  pills: BusinessUnitPill[];
  backgroundImage?: string;
  backgroundPosition?: string;
}

const businessUnits: BusinessUnit[] = [
  {
    title: "Wealth & Investments",
    description: "Retail investment solutions offered through direct, private clients and intermediary channels.",
    icon: iconWealth,
    backgroundImage: "https://images.pexels.com/photos/8862298/pexels-photo-8862298.jpeg",
    backgroundPosition: "center 40%",
    pills: [
      { value: "30k+", label: "clients" },
      { value: "4", label: "CIS funds" },
      { value: "7", label: "products" },
      { value: "4k", label: "active IFAs" },
      { value: "100+", label: "employees" },
    ],
  },
  {
    title: "Employee Benefits",
    description: "Group risk and retirement solutions including trust administration and other fiduciary services.",
    icon: iconGroup,
    backgroundImage: "https://images.pexels.com/photos/7328442/pexels-photo-7328442.jpeg",
    backgroundPosition: "center 55%",
    pills: [
      { value: "500+", label: "schemes" },
      { value: "750k+", label: "members" },
      { value: "65+", label: "employers" },
      { value: "80+", label: "employees" },
      { value: "160+", label: "IFAs" },
    ],
  },
  {
    title: "Private Capital",
    description: "Private capital solutions across four established & integrated strategies.",
    icon: iconAlternative,
    backgroundImage: "https://images.pexels.com/photos/30056229/pexels-photo-30056229.jpeg",
    backgroundPosition: "center 20%",
    pills: [
      { value: "310+", label: "clients" },
      { value: "6", label: "private funds" },
      { value: "42", label: "employees" },
      { value: "R5bn+", label: "pipeline" },
      { value: "13", label: "industries" },
    ],
  },
];

export default function BusinessUnitsSection() {
  // CMS data
  const sectionHeader = useSectionHeader('about', 'operating-businesses');

  // ⚠️ ANALYTICS: Section visibility tracking
  const { ref: sectionRef } = useSectionVisibility({
    sectionId: 'operating-businesses',
    sectionName: 'Operating Businesses',
    pageName: 'About',
    position: 3,
  });

  return (
    <section id="operating-businesses" ref={sectionRef} className="bg-background pt-16 pb-24 md:pt-24 md:pb-32">
      <div className="container mx-auto px-4 md:px-8">
        {/* molecule-section-header-centered-with-tag */}
        <div className="mb-12 flex flex-col items-center text-center">
          {/* atom-tag-subtle */}
          {sectionHeader?.tag_text && (
            <div className="mb-6">
              <Tag variant="subtle">{sectionHeader.tag_text}</Tag>
            </div>
          )}

          {/* atom-text-section-header + atom-text-section-header-emphasis */}
          <h2 className="mb-4 text-3xl font-light md:text-4xl lg:text-5xl">
            <RenderHeaderParts text={sectionHeader?.header_text || 'Three businesses, **one platform**'} />
          </h2>

          {/* atom-text-section-subheader */}
          {sectionHeader?.sub_header && (
            <p className="text-17 text-muted-foreground max-w-xl">
              {sectionHeader.sub_header}
            </p>
          )}
        </div>

        {/* component-card-business-unit grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {businessUnits.map((unit, index) => (
            <BusinessCardWrapper key={index} unit={unit} index={index}>
              <BusinessUnitCard
                title={unit.title}
                description={unit.description}
                icon={unit.icon}
                pills={unit.pills}
                backgroundImage={unit.backgroundImage}
                backgroundPosition={unit.backgroundPosition}
              />
            </BusinessCardWrapper>
          ))}
        </div>

        {/* atom-button-secondary: CTA Button */}
        <div className="mt-12 flex justify-center">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              // ⚠️ ANALYTICS: Track CTA click (FIX 9)
              trackEvent(ANALYTICS_EVENTS.CTA_BUTTON_CLICKED, {
                button_text: 'Learn more about Fedgroup',
                button_location: 'operating-businesses',
                button_page: '/about',
                destination_url: 'https://www.fedgroup.co.za/about-us',
                is_external: true,
              });
              window.open('https://www.fedgroup.co.za/about-us', '_blank', 'noopener,noreferrer');
            }}
          >
            Learn more about Fedgroup
            <ArrowUpRight weight="bold" />
          </Button>
        </div>
      </div>
    </section>
  );
}
